import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { apiClient, STORAGE_KEYS } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { useTranslation } from "react-i18next";

/**
 * Phase H2 + 2026-08 security hotfix — Email verification landing page.
 *
 * Modes:
 *   1. `?token=XYZ` → POST /api/auth/verify-email with that token.
 *      * First success (AuthResponse with access_token): drop the
 *        fresh JWT into localStorage, navigate to /verify-success.
 *      * Replay success (VerifyEmailAlreadyResponse with
 *        `requires_login=true`, NO tokens): show a friendly
 *        "already verified — please log in" screen with a login
 *        button (email pre-filled).
 *      Hits POST ONCE per token (idempotency-guard so React 18
 *      strict mode double-mount doesn't fire a second verify).
 *   2. `?status=ok`               → redirect after backend GET;
 *                                   currently unused by the SPA path
 *                                   but kept for compatibility.
 *   3. `?status=already_verified` → static "please log in" screen.
 *   4. `?status=expired|invalid`  → static error screen.
 *
 * SECURITY: a stale verification link MUST NOT become a login-magnet.
 * On the replay branch we do NOT touch localStorage tokens.
 */
export default function VerifyEmailPage() {
  const { t } = useTranslation("auth");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const token = params.get("token");
  const status = params.get("status"); // "ok" | "already_verified" | "expired" | "invalid"

  const [state, setState] = useState({
    phase: token ? "verifying" : status ? "static" : "no-token",
    error: null,
    status: status,
    prefillEmail: null,
  });

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.post("/auth/verify-email", { token });
        if (cancelled) return;
        if (data?.requires_login) {
          // Replay branch — DO NOT store tokens. Route the user to
          // the login form with their email pre-filled.
          setState({
            phase: "already_verified",
            status: "already_verified",
            error: null,
            prefillEmail: data?.email || null,
          });
          return;
        }
        // First-consume branch: token pair present → persist + go to app.
        localStorage.setItem(STORAGE_KEYS.access, data.access_token);
        localStorage.setItem(STORAGE_KEYS.refresh, data.refresh_token);
        localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(data.user));
        await refreshUser();
        if (!cancelled) navigate("/verify-success", { replace: true });
      } catch (err) {
        if (cancelled) return;
        const code = err?.response?.data?.error?.code;
        setState({
          phase: "error",
          status: code === "token_expired" ? "expired" : "invalid",
          error: err?.response?.data?.error?.message || t("verify.failed"),
          prefillEmail: null,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, refreshUser, navigate]);

  const onResend = async () => {
    const email = window.prompt(t("verify.resendPrompt"));
    if (!email) return;
    try {
      await apiClient.post("/auth/resend-verification", { email });
      window.alert(t("verify.resendGeneric"));
    } catch (err) {
      const msg =
        err?.response?.data?.error?.message ||
        t("verify.resendFailed");
      window.alert(msg);
    }
  };

  const goLogin = () => {
    const target = state.prefillEmail
      ? `/login?email=${encodeURIComponent(state.prefillEmail)}`
      : "/login";
    navigate(target);
  };

  if (state.phase === "verifying") {
    return (
      <div className="mx-auto max-w-md py-16 text-center" data-testid="verify-page-verifying">
        <div className="text-emerald-400 text-4xl mb-4">⋯</div>
        <h1 className="text-2xl font-medium text-zinc-100 mb-2">{t("verify.verifying")}</h1>
        <p className="text-sm text-zinc-400">{t("verify.momentPlease")}</p>
      </div>
    );
  }

  // Fresh success (rarely rendered — normally we navigate to /verify-success).
  if (state.phase === "success" || state.status === "ok") {
    return (
      <div className="mx-auto max-w-md py-16 text-center" data-testid="verify-page-success">
        <div className="text-emerald-400 text-4xl mb-4">✓</div>
        <h1 className="text-2xl font-medium text-zinc-100 mb-2">{t("verify.confirmedTitle")}</h1>
        <p className="text-sm text-zinc-400 mb-6">{t("verify.confirmedBody")}</p>
        <button
          onClick={() => navigate("/dashboard")}
          data-testid="verify-success-cta"
          className="rounded-md bg-emerald-500 px-6 py-2.5 text-sm font-medium text-zinc-950 hover:bg-emerald-400"
        >
          {t("verify.toDashboard")}
        </button>
      </div>
    );
  }

  // Idempotent replay — friendly login nudge, no session issued.
  if (state.phase === "already_verified" || state.status === "already_verified") {
    return (
      <div className="mx-auto max-w-md py-16 text-center" data-testid="verify-page-already">
        <div className="text-emerald-400 text-4xl mb-4">✓</div>
        <h1 className="text-2xl font-medium text-zinc-100 mb-2">
          {t("verify.alreadyTitle")}
        </h1>
        <p className="text-sm text-zinc-400 mb-6">
          {t("verify.alreadyBody")}
        </p>
        <div className="flex flex-col items-center gap-3">
          <button
            onClick={goLogin}
            data-testid="verify-goto-login-btn"
            className="rounded-md bg-emerald-500 px-6 py-2.5 text-sm font-medium text-zinc-950 hover:bg-emerald-400"
          >
            {t("verify.toLogin")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md py-16 text-center" data-testid="verify-page-error">
      <div className={state.status === "expired" ? "text-rose-400 text-4xl mb-4" : "text-amber-400 text-4xl mb-4"}>
        {state.status === "expired" ? "⚠" : "ⓘ"}
      </div>
      <h1 className="text-2xl font-medium text-zinc-100 mb-2">
        {state.status === "expired" ? t("verify.linkExpired") : t("verify.linkInvalid")}
      </h1>
      <p className="text-sm text-zinc-400 mb-6">
        {state.status === "expired"
          ? t("verify.expiredBody")
          : state.error || t("verify.invalidBody")}
      </p>
      <div className="flex flex-col items-center gap-3">
        {state.status === "invalid" ? (
          <>
            <button
              onClick={() => navigate("/login")}
              data-testid="verify-goto-login-btn"
              className="rounded-md bg-emerald-500 px-6 py-2.5 text-sm font-medium text-zinc-950 hover:bg-emerald-400"
            >
              {t("verify.toLogin")}
            </button>
            <button
              onClick={onResend}
              data-testid="verify-resend-btn"
              className="text-xs text-zinc-500 underline-offset-4 hover:text-zinc-300 hover:underline"
            >
              {t("verify.requestNew")}
            </button>
          </>
        ) : (
          <>
            <button
              onClick={onResend}
              data-testid="verify-resend-btn"
              className="rounded-md border border-emerald-500/60 px-6 py-2.5 text-sm font-medium text-emerald-300 hover:bg-emerald-500/10"
            >
              {t("verify.requestNew")}
            </button>
            <Link
              to="/login"
              className="text-xs text-zinc-500 underline-offset-4 hover:text-zinc-300 hover:underline"
            >
              {t("verify.toLogin")}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
